// Public admin HTTP contracts. Keep aligned with the backend WhatsApp modules.
export const WHATSAPP_PURPOSES = [
  "order_confirmation",
  "order_placed",
  "back_in_stock",
  "abandoned_recovery",
  "customer_password_reset",
  "customer_phone_change",
  "restaurant_delivery_confirmation",
  "restaurant_delivery_received",
] as const;
export type WhatsappPurpose = (typeof WHATSAPP_PURPOSES)[number];
export const WHATSAPP_STATUSES = [
  "pending",
  "accepted",
  "unknown",
  "sent",
  "delivered",
  "read",
  "failed",
] as const;
export type WhatsappMessageStatus = (typeof WHATSAPP_STATUSES)[number];
export type WhatsappAcceptance =
  | "pending"
  | "accepted"
  | "rejected"
  | "unknown";
export type WhatsappDeliveryStatus =
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "deleted"
  | "unknown";
export type WhatsappBillingClassification = "billable" | "free" | "unknown";
export type WhatsappDateRange = { dateFrom?: string; dateTo?: string };
export type WhatsappMessageFilters = WhatsappDateRange & {
  page?: number;
  limit?: number;
  sellerId?: string;
  unattributed?: boolean;
  storeId?: string;
  purpose?: WhatsappPurpose;
  template?: string;
  status?: WhatsappMessageStatus;
  acceptance?: WhatsappAcceptance;
  messageId?: string;
};
export type WhatsappUsageFilters = WhatsappMessageFilters & {
  groupBy?: "seller" | "store";
  search?: string;
};
export type WhatsappResolvedRange = {
  dateFrom: string;
  dateTo: string;
  from: Date;
  until: Date;
};
export type WhatsappResolvedFilters = WhatsappResolvedRange &
  Omit<
    WhatsappUsageFilters,
    keyof WhatsappDateRange | "page" | "limit" | "groupBy"
  > & { page: number; limit: number; groupBy: "seller" | "store" };
export type WhatsappCoverage = {
  recordedSince: string | null;
  retainedSince: string;
  retentionDays: number;
  historicalBackfill: false;
  scope: "recorded_attempts_only";
};
export type WhatsappCounts = {
  attempts: number;
  accepted: number;
  httpAccepted: number;
  requestUnknown: number;
  deliveryFailed: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  pending: number;
  unknown: number;
  rejected: number;
  billable: number;
  free: number;
  billingUnknown: number;
};
export type WhatsappMessage = {
  id: string;
  storeId: string | null;
  storeName: string | null;
  sellerId: string | null;
  sellerName: string | null;
  purpose: WhatsappPurpose;
  templateName: string;
  languageCode: string;
  recipientMasked: string;
  phoneNumberId: string;
  businessAccountId: string | null;
  requestOutcome: WhatsappAcceptance;
  httpAcceptedAt: string | null;
  providerMessageId: string | null;
  acceptance: WhatsappAcceptance;
  status: WhatsappMessageStatus;
  attemptedAt: string;
  acceptedAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failedAt: string | null;
  errorCode: string | null;
  billingClassification: WhatsappBillingClassification;
  pricingModel: string | null;
  pricingType: string | null;
  pricingCategory: string | null;
};
export type WhatsappReceipt = {
  status: WhatsappDeliveryStatus;
  providerStatus: string | null;
  errors: WhatsappMetaHistoryEvent["errors"];
  billable: boolean | null;
  conversationId: string | null;
  conversationOrigin: string | null;
  conversationExpiresAt: string | null;
  occurredAt: string;
  receivedAt: string;
  errorCode: string | null;
  billingClassification: WhatsappBillingClassification;
  pricingModel: string | null;
  pricingType: string | null;
  pricingCategory: string | null;
};
export type WhatsappMessageDetail = WhatsappMessage & {
  receipts: WhatsappReceipt[];
  receiptsTruncated: boolean;
};
export type WhatsappMessagePage = {
  items: WhatsappMessage[];
  total: number;
  page: number;
  limit: number;
  dateFrom: string;
  dateTo: string;
  coverage: WhatsappCoverage;
};
export type WhatsappOverview = {
  dateFrom: string;
  dateTo: string;
  coverage: WhatsappCoverage;
  counts: WhatsappCounts;
};
export type WhatsappUsageRow = WhatsappCounts & {
  id: string | null;
  name: string | null;
  unattributed: boolean;
  sellerId: string | null;
  sellerName: string | null;
};
export type WhatsappUsagePage = {
  items: WhatsappUsageRow[];
  total: number;
  page: number;
  limit: number;
  groupBy: "seller" | "store";
  dateFrom: string;
  dateTo: string;
  coverage: WhatsappCoverage;
};

export type WhatsappJson =
  | null
  | boolean
  | number
  | string
  | WhatsappJson[]
  | { [key: string]: WhatsappJson };
export type WhatsappTemplateComponent = {
  type: string;
  [key: string]: WhatsappJson;
};
export type WhatsappTemplateCategory =
  | "UTILITY"
  | "MARKETING"
  | "AUTHENTICATION";
export type WhatsappParameterFormat = "NAMED" | "POSITIONAL";
export type WhatsappTemplate = {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string;
  parameter_format?: string;
  components: WhatsappTemplateComponent[];
  rejected_reason?: string;
};
export type WhatsappTemplateFilters = {
  after?: string;
  limit?: number;
  name?: string;
  status?: string;
  category?: WhatsappTemplateCategory;
  language?: string;
};
export type WhatsappTemplatePage = {
  items: WhatsappTemplate[];
  after: string | null;
};
export type WhatsappCreateTemplate = {
  name: string;
  language: string;
  category: WhatsappTemplateCategory;
  parameter_format?: WhatsappParameterFormat;
  components: WhatsappTemplateComponent[];
};
export type WhatsappEditTemplate = {
  parameter_format?: WhatsappParameterFormat;
  components: WhatsappTemplateComponent[];
};
export type WhatsappBinding = {
  purpose: WhatsappPurpose;
  templateName: string;
  languageCode: string;
  enabled: boolean;
  requiredBodyParameters: string[];
  requiredQuickReplyButtons: number;
  requiredQuickReplyLabels: Array<{ index: number; labels: string[] }>;
  requiredUrlButton: { index: number; url: string } | null;
};
export type WhatsappBindingInput = { templateId?: string; disabled?: boolean };
export type WhatsappAccount = {
  readiness: {
    tokenConfigured: boolean;
    phoneNumberIdConfigured: boolean;
    businessAccountIdConfigured: boolean;
    appSecretConfigured: boolean;
    webhookVerifyTokenConfigured: boolean;
    managementReady: boolean;
  };
  businessAccountId: string | null;
  phoneNumberId: string | null;
  managementApiVersion: string;
  bindings: WhatsappBinding[];
  links: { manager: string; billing: string };
};
export type WhatsappBillingPoint = {
  dateFrom: string;
  dateTo: string;
  category: string;
  pricingType: string;
  volume: number;
  approximateCost: number | null;
};
export type WhatsappBillingBreakdown = {
  key: string;
  volume: number;
  approximateCost: number | null;
};
export type WhatsappBilling = {
  dateFrom: string;
  dateTo: string;
  fetchedAt: string;
  dataThrough: string | null;
  mayBeDelayed: true;
  scope: "business_account";
  businessAccountId: string;
  currency: string | null;
  costKind: "meta_reported_approximate";
  costAvailability: "available" | "partial" | "unavailable";
  volume: number | null;
  approximateCost: number | null;
  points: WhatsappBillingPoint[];
  byDate: WhatsappBillingBreakdown[];
  byCategory: WhatsappBillingBreakdown[];
  billingUrl: string;
};

export type WhatsappTemplateAcknowledgment = {
  id: string;
  status?: string;
  category?: string;
};

export type WhatsappMetaAnalytics = {
  source: "meta";
  dateFrom: string;
  dateTo: string;
  fetchedAt: string;
  sent: number | null;
  delivered: number | null;
  points: Array<{
    start: number;
    end: number;
    sent: number;
    delivered: number;
  }>;
};
export type WhatsappMetaHistoryEvent = {
  status: string;
  providerStatus: string | null;
  timestamp: string | null;
  receivedAt: string;
  errorCode: string | null;
  errors: Array<{
    code: string | null;
    title: string | null;
    message: string | null;
    details: string | null;
  }>;
  billable: boolean | null;
  pricingModel: string | null;
  pricingType: string | null;
  pricingCategory: string | null;
  conversationId: string | null;
  conversationOrigin: string | null;
  conversationExpiresAt: string | null;
};
export type WhatsappMetaHistoryItem = {
  id: string;
  messageId: string;
  phoneNumberId: string;
  businessAccountId: string | null;
  recipientMasked: string | null;
  context: {
    attemptId: string;
    attemptedAt: string;
    acceptedAt: string | null;
    acceptance: WhatsappAcceptance;
    purpose: WhatsappPurpose;
    templateName: string;
    languageCode: string;
    storeId: string | null;
    storeName: string | null;
    sellerId: string | null;
    sellerName: string | null;
  } | null;
  events: WhatsappMetaHistoryEvent[];
  eventsTruncated: boolean;
};
export type WhatsappMetaHistory = {
  source: "meta_webhook";
  scanned: number;
  eventsTruncated: boolean;
  items: WhatsappMetaHistoryItem[];
  after: string | null;
  coverage: {
    recordedSince: string | null;
    retentionDays: number;
    historicalBackfill: false;
  };
};
